import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Cloud,
  CloudFog,
  CloudLightning,
  CloudRain,
  CloudSnow,
  CloudSun,
  LocateFixed,
  MapPin,
  Search,
  Sun,
  Wind,
  X,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useWeather, type SavedCity } from '@/hooks/useWeather';
import {
  describeWeatherCode,
  geocodeCity,
  reverseGeocode,
} from '@/lib/weather';
import { cn } from '@/lib/utils';

function iconForCode(code: number): typeof Sun {
  if (code === 0 || code === 1) return Sun;
  if (code === 2) return CloudSun;
  if (code === 3) return Cloud;
  if (code === 45 || code === 48) return CloudFog;
  if (code >= 51 && code <= 67) return CloudRain;
  if (code >= 71 && code <= 77) return CloudSnow;
  if (code >= 80 && code <= 82) return CloudRain;
  if (code >= 85 && code <= 86) return CloudSnow;
  if (code >= 95) return CloudLightning;
  return Cloud;
}

const WEEKDAY = new Intl.DateTimeFormat('pt-BR', { weekday: 'short' });
function dayLabel(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return 'Hoje';
  return WEEKDAY.format(d).replace('.', '').toLowerCase();
}

export function WeatherWidget() {
  const { city, setCity, snapshot, isLoading } = useWeather();
  const [open, setOpen] = useState(false);
  const Icon = snapshot ? iconForCode(snapshot.current.weatherCode) : Cloud;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={
          snapshot
            ? `${describeWeatherCode(snapshot.current.weatherCode)} em ${city.name}`
            : 'Clima'
        }
        className={cn(
          'inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5',
          'text-xs font-semibold transition-colors hover:bg-accent',
        )}
      >
        <Icon className="h-4 w-4 text-primary" />
        <span className="font-bold">
          {isLoading || !snapshot ? '—' : `${Math.round(snapshot.current.temperature)}°`}
        </span>
        <span className="hidden text-muted-foreground sm:inline">{city.name}</span>
      </button>

      {open && (
        <WeatherPanel
          city={city}
          onClose={() => setOpen(false)}
          onPickCity={(c) => setCity(c)}
        />
      )}
    </>
  );
}

function WeatherPanel({
  city,
  onClose,
  onPickCity,
}: {
  city: SavedCity;
  onClose: () => void;
  onPickCity: (c: SavedCity) => void;
}) {
  const { snapshot, isLoading } = useWeather();
  const [view, setView] = useState<'main' | 'search'>('main');

  const Icon = snapshot ? iconForCode(snapshot.current.weatherCode) : Cloud;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-background/60 p-4 pt-20"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl bg-card p-5 shadow-xl ring-1 ring-border"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mb-4 flex items-start justify-between gap-2">
          <div>
            <h3 className="text-base font-bold">
              {city.name}
              {city.admin1 && city.admin1 !== city.name && (
                <span className="ml-1 font-medium text-muted-foreground">· {city.admin1}</span>
              )}
            </h3>
            {city.country && (
              <p className="text-xs text-muted-foreground">{city.country}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {view === 'main' ? (
          <>
            {isLoading || !snapshot ? (
              <p className="py-6 text-center text-sm text-muted-foreground">Carregando…</p>
            ) : (
              <>
                <div className="mb-4 flex items-center gap-4">
                  <Icon className="h-14 w-14 text-primary" />
                  <div>
                    <p className="text-4xl font-bold leading-none">
                      {Math.round(snapshot.current.temperature)}°
                    </p>
                    <p className="text-sm font-medium text-foreground/80">
                      {describeWeatherCode(snapshot.current.weatherCode)}
                    </p>
                    {snapshot.current.windSpeed != null && (
                      <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                        <Wind className="h-3 w-3" />
                        {Math.round(snapshot.current.windSpeed)} km/h
                      </p>
                    )}
                  </div>
                </div>

                {snapshot.daily.length > 0 && (
                  <div className="mb-4">
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Próximos dias
                    </p>
                    <div className="grid grid-cols-5 gap-2">
                      {snapshot.daily.slice(0, 5).map((d) => {
                        const DayIcon = iconForCode(d.weatherCode);
                        return (
                          <div
                            key={d.date}
                            className="flex flex-col items-center gap-1 rounded-md border border-border bg-secondary/40 p-2"
                            title={describeWeatherCode(d.weatherCode)}
                          >
                            <span className="text-[10px] font-semibold uppercase text-muted-foreground">
                              {dayLabel(d.date)}
                            </span>
                            <DayIcon className="h-5 w-5 text-primary" />
                            <span className="text-sm font-bold leading-none">
                              {Math.round(d.tempMax)}°
                            </span>
                            <span className="text-[10px] text-muted-foreground">
                              {Math.round(d.tempMin)}°
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="grid grid-cols-2 gap-2">
              <UseLocationButton onPick={onPickCity} />
              <button
                type="button"
                onClick={() => setView('search')}
                className="inline-flex items-center justify-center gap-1.5 rounded-md border border-border bg-card px-3 py-2 text-xs font-semibold hover:bg-accent"
              >
                <Search className="h-4 w-4" />
                Trocar cidade
              </button>
            </div>
          </>
        ) : (
          <CitySearch
            onCancel={() => setView('main')}
            onPick={(c) => {
              onPickCity(c);
              onClose();
            }}
          />
        )}
      </div>
    </div>
  );
}

function UseLocationButton({ onPick }: { onPick: (c: SavedCity) => void }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handle = () => {
    if (!('geolocation' in navigator)) {
      setError('Geolocalização não suportada neste navegador.');
      return;
    }
    setLoading(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        const reverse = await reverseGeocode(lat, lng);
        onPick({
          name: reverse?.name ?? 'Minha localização',
          admin1: reverse?.admin1,
          country: reverse?.country,
          latitude: lat,
          longitude: lng,
        });
        setLoading(false);
      },
      (err) => {
        setError(err.message || 'Não foi possível obter localização.');
        setLoading(false);
      },
      { enableHighAccuracy: false, timeout: 10000 },
    );
  };

  return (
    <div>
      <button
        type="button"
        onClick={handle}
        disabled={loading}
        className="inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-border bg-card px-3 py-2 text-xs font-semibold hover:bg-accent disabled:opacity-50"
      >
        <LocateFixed className="h-4 w-4" />
        {loading ? 'Localizando…' : 'Minha localização'}
      </button>
      {error && <p className="mt-1 text-[10px] text-destructive">{error}</p>}
    </div>
  );
}

function CitySearch({
  onCancel,
  onPick,
}: {
  onCancel: () => void;
  onPick: (c: SavedCity) => void;
}) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  const search = useQuery({
    queryKey: ['geocode', debounced],
    queryFn: ({ signal }) => geocodeCity(debounced, signal),
    enabled: debounced.length >= 2,
  });

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar cidade (mín. 2 letras)"
          className="pl-9"
          autoFocus
        />
      </div>

      {debounced.length < 2 ? (
        <p className="px-1 py-3 text-xs text-muted-foreground">
          Digite ao menos 2 letras pra buscar.
        </p>
      ) : search.isLoading ? (
        <p className="px-1 py-3 text-xs text-muted-foreground">Buscando…</p>
      ) : search.error ? (
        <p className="px-1 py-3 text-xs text-destructive">Falha na busca.</p>
      ) : !search.data || search.data.length === 0 ? (
        <p className="px-1 py-3 text-xs text-muted-foreground">Nenhuma cidade encontrada.</p>
      ) : (
        <ul className="max-h-72 divide-y divide-border overflow-y-auto rounded-md border border-border">
          {search.data.map((g) => (
            <li key={g.id}>
              <button
                type="button"
                onClick={() =>
                  onPick({
                    name: g.name,
                    admin1: g.admin1,
                    country: g.country,
                    latitude: g.latitude,
                    longitude: g.longitude,
                  })
                }
                className="flex w-full items-start gap-2 p-3 text-left text-sm hover:bg-accent"
              >
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div>
                  <p className="font-semibold">{g.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {[g.admin1, g.country].filter(Boolean).join(', ')}
                  </p>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        onClick={onCancel}
        className="text-xs font-semibold text-primary hover:underline"
      >
        Voltar
      </button>
    </div>
  );
}
